import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';
import { useOptibotStore } from '../stores/optibot-store';
import { OPTIBOT_GRADIENT } from '../utils/constants';

/**
 * Floating action button that toggles the OptiBot chat panel.
 * Renders a 60x60 gradient circle at the bottom-right corner.
 * Gated: defense-in-depth VITE_ENABLE_OPTIBOT check (primary gate is in AppShell) + token check.
 * Also registers Ctrl/Cmd+Shift+O keyboard shortcut to toggle the panel.
 */
export function OptiBotFAB() {
  const { t } = useTranslation();
  const token = useAuthStore((s) => s.token);
  const { isOpen, toggle } = useOptibotStore();

  // Keyboard shortcut: Ctrl/Cmd+Shift+O to toggle OptiBot panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'KeyO') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);

  // Defense-in-depth: primary gate is in AppShell, this is a secondary check
  if (import.meta.env.VITE_ENABLE_OPTIBOT !== 'true') return null;
  if (!token) return null;
  // Hide FAB when panel is open — the panel header already has a close button
  if (isOpen) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid="optibot-fab"
            variant="ghost"
            onClick={toggle}
            aria-label={t('optibot.openPanel')}
            className={cn(
              'group fixed bottom-6 right-6 z-40',
              'h-[58px] w-[58px] rounded-full p-0',
              // Keep the icon white on hover (ghost variant would darken it); the
              // hover affordance is the circle + icon growing, not a color change.
              'text-white shadow-lg transition-transform duration-150 hover:scale-105 hover:text-white',
            )}
            style={{
              background: OPTIBOT_GRADIENT,
            }}
          >
            <MessageCircle
              className="h-6 w-6 transition-transform duration-150 group-hover:scale-110"
              strokeWidth={2.5}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end">
          {t('optibot.fabTooltip')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
